import {
  UpdatableRecommendationStatus,
  useGetCartProductsQuery,
  useGetRecommendationsByRequestPatientCodexIdQuery,
  useUpdateRecommendationStatusMutation,
} from 'graphql/generated/remote-schema-hasura';
import { useMemo } from 'react';
import moment from 'moment';
import {
  Exact,
  InputMaybe,
  ServiceRequestStatus,
} from 'graphql/generated/hasura';
import { useCustomToast } from 'layout/useToast';

export type RecommendationData = {
  isPurchased: boolean;
  hasInCart: boolean;
  showArchive: boolean;
  createdAt: string;
  id: string;
  productTitle: string;
  providerCodexId: string;
  providerName: string;
  quantity: number;
  status: ServiceRequestStatus;
  productId: number;
  totalPrice: number;
  imageUrl?: string;
};

const isThreeMonthsPassed = (dateFromBackend: string): boolean => {
  return moment().diff(moment(dateFromBackend), 'months') >= 3;
};

type IVars =
  | Exact<{
      limit?: InputMaybe<number> | undefined;
      offset?: InputMaybe<number> | undefined;
      statuses?:
        | InputMaybe<ServiceRequestStatus | ServiceRequestStatus[]>
        | undefined;
    }>
  | undefined;

export const useRecommendation = (variables?: IVars) => {
  const { addToast } = useCustomToast();
  const [updateRecommendationStatus] = useUpdateRecommendationStatusMutation({
    refetchQueries: ['GetRecommendationsByRequestPatientCodexId'],
    onError: (error) => addToast({ type: 'warning', message: error.message }),
  });

  const { data: card } = useGetCartProductsQuery();

  const { data, loading, refetch } =
    useGetRecommendationsByRequestPatientCodexIdQuery({
      variables,
      fetchPolicy: 'cache-and-network',
    });

  const moveToArchive = (recommendationId: string) => {
    return updateRecommendationStatus({
      variables: {
        recommendationId,
        status: UpdatableRecommendationStatus.OnHold,
      },
    });
  };

  const handleDeleteRecommendation = (recommendationId: string) => {
    return updateRecommendationStatus({
      variables: {
        recommendationId,
        status: UpdatableRecommendationStatus.Revoked,
      },
    });
  };

  const mappedData = useMemo(() => {
    return data?.getRecommendationsByRequestPatientCodexId.recommendations.map(
      (recommendation) => {
        return {
          ...recommendation,
          isPurchased: recommendation.status === ServiceRequestStatus.Completed,
          hasInCart: card?.getCartProducts?.cart_products?.some(
            (productCart) =>
              productCart.product_id === recommendation?.productId &&
              productCart?.patientOwner?.id === recommendation?.patientCodexId,
          ),
          showArchive:
            isThreeMonthsPassed(recommendation.createdAt) &&
            recommendation.status === ServiceRequestStatus.Active,
        } as RecommendationData;
      },
    );
  }, [data, card]);

  return {
    loading,
    refetch,
    data: mappedData,
    total: data?.getRecommendationsByRequestPatientCodexId.total,
    moveToArchive,
    handleDeleteRecommendation,
  };
};
